import { differs } from "@/components/molecules/form-footer";
import { toolConfiguration } from "@/features/tools/hooks/use-tool-activation";
import {
  useDeleteAgentConfiguration,
  useUpdateAgentConfiguration,
  useUpdateClientTool,
} from "@/features/tools/hooks/use-tools";
import type { AvailableTool } from "@/features/tools/types";
import { useTranslation } from "@/features/utils/i18n";
import { useRef, useState } from "react";
import { toast } from "sonner";
import type { DynamicFormProps } from "../components/dynamic-form";
import { useToolConfiguration } from "./use-tool-configuration";

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/**
 * A module's settings form — for the whole company, or, with `agentId`, for one
 * agent on top of the company's.
 *
 * Saved the way the company settings are: every edit stays in the form, the
 * page's `FormFooter` comes up as soon as something differs from what is saved
 * (« Annuler · Enregistrer »), and leaving with edits asks first. It replaced
 * an autosave that sent a switch at once and held a text field as a draft with
 * its own « Enregistrer » beside it — two ways of saving on one form, and a
 * status line to watch to know which had happened.
 *
 * At the agent level a field left `undefined` inherits the company's value, so
 * what is stored is only what this agent does differently; once every field
 * inherits again, the agent's override is removed rather than kept empty.
 */
export function useToolSettings({
  availableTool,
  agentId,
}: {
  availableTool?: AvailableTool;
  agentId?: string;
}) {
  const { t } = useTranslation("tools");
  const updateClientTool = useUpdateClientTool();
  const updateAgentConfiguration = useUpdateAgentConfiguration();
  const deleteAgentConfiguration = useDeleteAgentConfiguration();

  const tool = availableTool?.tool;
  const clientTool = availableTool?.clientTool;
  const isActive = clientTool?.enabled || false;
  const agentOverride = agentId ? clientTool?.per_agents?.[agentId] : undefined;

  // `formData` is what the form shows; `saved` what was last loaded or sent.
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [saved, setSaved] = useState<Record<string, unknown>>({});
  const formRef = useRef<HTMLFieldSetElement>(null);

  // The form is loaded once per tool, activation and level, not on every
  // refetch: a save refetches the list, and reloading the form from it would
  // take back an edit made while the save was on its way.
  const loadKey = tool
    ? `${tool.id}:${clientTool?.id ?? ""}:${agentId ?? ""}`
    : "";
  const [loadedKey, setLoadedKey] = useState("");
  if (tool && loadKey !== loadedKey) {
    setLoadedKey(loadKey);
    const loaded = agentId
      ? { ...(agentOverride?.configuration || {}) }
      : toolConfiguration(tool, clientTool);
    setFormData(loaded);
    setSaved(loaded);
  }

  const { customFields, fieldVisibility, fieldGroups } =
    useToolConfiguration(tool);

  /** What an agent inherits: the tool's defaults under the company's settings. */
  const parentData = tool ? toolConfiguration(tool, clientTool) : {};

  const dirty = isActive && differs(formData, saved);
  const saving =
    updateClientTool.isPending ||
    updateAgentConfiguration.isPending ||
    deleteAgentConfiguration.isPending;

  const save = async () => {
    if (!tool || !clientTool || !isActive) return;

    // A required field left empty, or an input the browser rejects (a pattern,
    // an e-mail, a bound), keeps the form from saving: the browser says which
    // next to the field. An agent's empty field is not missing, it inherits.
    const visibility: DynamicFormProps["fieldVisibility"] = fieldVisibility;
    const required = agentId ? [] : (tool.schema.required ?? []);
    const merged = agentId ? { ...parentData, ...defined(formData) } : formData;
    const missing = required.find(
      (name) =>
        (visibility?.[name]?.(merged) ?? true) && isEmpty(formData[name]),
    );
    const invalid =
      formRef.current?.querySelector<HTMLInputElement>(":invalid") ??
      (missing
        ? formRef.current?.querySelector<HTMLInputElement>(
            `#${CSS.escape(missing)}`,
          )
        : null);
    if (missing || invalid) {
      invalid?.reportValidity();
      invalid?.focus();
      toast.error(t("tools.messages.invalid"));
      return;
    }

    const configuration = formData;
    try {
      if (!agentId) {
        await updateClientTool.mutateAsync({
          id: clientTool.id,
          data: { configuration },
        });
      } else {
        const overrides = defined(configuration);
        if (
          Object.keys(overrides).length === 0 &&
          agentOverride?.enabled === undefined
        ) {
          await deleteAgentConfiguration.mutateAsync({
            id: clientTool.id,
            agentId,
          });
        } else {
          await updateAgentConfiguration.mutateAsync({
            id: clientTool.id,
            agentId,
            agentConfig: { configuration: overrides },
          });
        }
      }
      setSaved(configuration);
      toast.success(t("tools.messages.saved"));
    } catch (error) {
      // A refusal the server explains carries its code as the message.
      toast.error(
        error instanceof Error
          ? t(`tools.messages.errors.${error.message}`, {
              defaultValue: error.message,
            })
          : t("tools.messages.saveError"),
      );
    }
  };

  /** The form back to what is saved. */
  const reset = () => setFormData(saved);

  /** Every field of this agent back on the company's value. */
  const resetToCompany = async () => {
    if (!agentId || !clientTool) return;
    setFormData({});
    setSaved({});
    try {
      if (agentOverride?.enabled === undefined) {
        await deleteAgentConfiguration.mutateAsync({
          id: clientTool.id,
          agentId,
        });
      } else {
        await updateAgentConfiguration.mutateAsync({
          id: clientTool.id,
          agentId,
          agentConfig: { configuration: {} },
        });
      }
      toast.success(t("tools.messages.agentOverrideRemoved"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("tools.messages.resetError"),
      );
    }
  };

  return {
    tool,
    isActive,
    formData,
    formRef,
    handleChange: setFormData,
    dirty,
    saving,
    save,
    reset,
    customFields,
    fieldVisibility,
    fieldGroups,
    parentData,
    /** How many fields this agent does differently, as saved. */
    agentOverrideCount: Object.keys(defined(saved)).length,
    resetToCompany,
  };
}

/** The fields an agent actually overrides: an `undefined` one inherits. */
const defined = (values: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  );
